import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Button,
  Dropdown,
  Heading,
  IconChevron,
  IconFilter,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import translations from '../../../../client/.vocab';
import translations_tal from '../.vocab';
import { useFields } from './useFields';
import { useScreenSize } from 'src/client/hooks/useScreenSize';
import { useState } from 'react';

interface FilterProp {
  filterLabel?: string;
  filterPlaceholder?: string;
}

interface DriverFilterProp extends FilterProp {
  optionFilter: FilterProp;
}

export interface DriverOption {
  name: string;
}
export interface Driver {
  name: string;
  options: DriverOption[];
}
export interface DriverList {
  drivers: Driver[];
}

interface Props {
  driverList: DriverList;
  handleSubmit: any;
  filterTitle: string;
  totalFilters: number;
}

export const QueryFilter = ({
  driverList,
  handleSubmit,
  filterTitle = 'Filter drivers',
  totalFilters = 0,
}: Props) => {
  const { t } = useTranslations(translations);
  const { t: t_tal } = useTranslations(translations_tal);
  const [mobileFilterDisplay, setMobileFilterDisplay] = useState(false);

  const primaryDriverList: DriverList = {
    drivers: [{ name: 'ALL', options: [] }, ...driverList.drivers],
  };

  const secondaryDriverList: DriverList = { ...driverList };

  const primaryDriverFilter: DriverFilterProp = {
    filterLabel: t_tal('Expore drivers by'),
    filterPlaceholder: t('Select a filter'),
    optionFilter: {
      filterLabel: t('Select up to two options'),
      filterPlaceholder: t('Select options'),
    },
  };

  const secondaryDriverFilter: DriverFilterProp = {
    filterLabel: t('Filter by'),
    filterPlaceholder: t('Select a filter'),
    optionFilter: {
      filterLabel: t('Select up to two options'),
      filterPlaceholder: t('Select options'),
    },
  };

  const { primaryDriver, secondaryDriver, primaryOptions, secondaryOptions } =
    useFields();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = handleSubmit((formValues: any) => {
    // eslint-disable-next-line
    console.log('SUCCESS:', formValues);
  });

  const isMobile = useScreenSize().isMobile;

  return (
    <>
      {isMobile ? (
        <>
          {!mobileFilterDisplay ? (
            <Box
              boxShadow="borderNeutralLarge"
              borderRadius={'large'}
              padding={'medium'}
              display={'flex'}
              style={{ width: '350px' }}
              onClick={() => {
                setMobileFilterDisplay(true);
              }}
            >
              <Inline space={'small'}>
                <Box style={{ marginTop: '-2px' }}>
                  {' '}
                  <IconFilter />
                </Box>

                <Box style={{ marginTop: '5px' }}>
                  <Text weight="strong">{filterTitle}</Text>
                </Box>
                <Box style={{ marginTop: '2px' }}>
                  <Badge tone="neutral">{totalFilters}</Badge>
                </Box>
              </Inline>
              <Box style={{ marginTop: '-2px', marginLeft: 'auto' }}>
                <IconChevron direction={'right'} />
              </Box>
            </Box>
          ) : (
            <Box style={{ width: '350px' }}>
              <Stack space="medium">
                <Heading level="4">{t('Filters')}</Heading>
                <Text>
                  {t_tal(
                    'Select filter to uncover deeper insights about candidates',
                  )}
                </Text>
                <Text>this is mobile</Text>
                <DriverFilter
                  driver={primaryDriver}
                  driverList={primaryDriverList}
                  driverOptions={primaryOptions}
                  filterProp={primaryDriverFilter}
                  primaryDisable={false}
                  secondaryDisable={primaryDriver.value === 'ALL'}
                />

                <DriverFilter
                  driver={secondaryDriver}
                  driverList={secondaryDriverList}
                  driverOptions={secondaryOptions}
                  filterProp={secondaryDriverFilter}
                  primaryDisable={
                    primaryDriver.value === 'ALL' || primaryDriver.value === ''
                  }
                  secondaryDisable={
                    !(
                      secondaryDriver.value !== '' &&
                      primaryOptions.value !== ''
                    )
                  }
                />

                <Inline space="small">
                  <Button variant="solid" type="submit">
                    {t('Apply filters')}
                  </Button>
                  <Button variant="transparent">{t('Clear all')}</Button>
                </Inline>
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <Box style={{ width: '350px' }}>
          <Stack space="medium">
            <Heading level="4">{t('Filters')}</Heading>
            <Text>
              {t_tal(
                'Select filter to uncover deeper insights about candidates',
              )}
            </Text>
            <DriverFilter
              driver={primaryDriver}
              driverList={primaryDriverList}
              driverOptions={primaryOptions}
              filterProp={primaryDriverFilter}
              primaryDisable={false}
              secondaryDisable={primaryDriver.value === 'ALL'}
            />

            <DriverFilter
              driver={secondaryDriver}
              driverList={secondaryDriverList}
              driverOptions={secondaryOptions}
              filterProp={secondaryDriverFilter}
              primaryDisable={
                primaryDriver.value === 'ALL' || primaryDriver.value === ''
              }
              secondaryDisable={
                !(secondaryDriver.value !== '' && primaryOptions.value !== '')
              }
            />

            <Inline space="small">
              <Button variant="solid" type="submit">
                {t('Apply filters')}
              </Button>
              <Button variant="transparent">{t('Clear all')}</Button>
            </Inline>
          </Stack>
        </Box>
      )}
    </>
  );
};

interface FilterFilterProp {
  driver: any;
  driverList: DriverList;
  driverOptions: any;
  filterProp: DriverFilterProp;
  primaryDisable: boolean;
  secondaryDisable: boolean;
}

const DriverFilter = ({
  filterProp,
  driverList,
  primaryDisable,
  secondaryDisable,
  driverOptions,
  driver,
}: FilterFilterProp) => {
  const options = driverList.drivers.find(
    (d) => d.name === driver.value,
  )?.options;
  return (
    <Stack space="medium">
      <Dropdown
        id="PRIMARY_FILTER"
        label={filterProp.filterLabel}
        onChange={driver.onChange}
        placeholder={filterProp.filterPlaceholder}
        value={driver.value}
        disabled={primaryDisable}
        tone={!driver.valid ? 'critical' : 'neutral'}
        message={!driver.valid && driver.errorMessage}
      >
        <>
          {driverList.drivers.map((d) => (
            <option key={d.name} value={d.name}>
              {d.name}
            </option>
          ))}
        </>
      </Dropdown>

      <Dropdown
        id="PRIMARY_OPTION_FILTER"
        label={filterProp.optionFilter.filterLabel}
        onChange={driverOptions.onChange}
        placeholder={filterProp.optionFilter.filterPlaceholder}
        value={driverOptions.value}
        disabled={secondaryDisable}
        tone={!driverOptions.valid ? 'critical' : 'neutral'}
        message={!driverOptions.valid && driverOptions.errorMessage}
      >
        <>
          {options &&
            options.map((option) => (
              <option key={option.name} value={option.name}>
                {option.name}
              </option>
            ))}
        </>
      </Dropdown>
    </Stack>
  );
};
